import axios from 'axios'
import type { AxiosResponse } from 'axios'
import { useGameStore } from '@/stores/gameStore'
import { debounce } from 'lodash'
import { useNewsStore } from '@/stores/newsStore.ts'
import { useNewsSettingsStore } from '@/stores/newsSettingsStore.ts'
import * as Sentry from '@sentry/vue'
import type NewsItem from '@/types/NewsItem.ts'
import type ChatRoom from '@/types/ChatRoom.ts'
import { logger } from '@/models/logger.ts'

export default class Backend {
  private baseUrl = import.meta.env.VITE_API_URL
  private gameStore = useGameStore()
  private newsStore = useNewsStore()
  private newsSettingsStore = useNewsSettingsStore()
  private debouncedPostRequest: (path: string, data: any) => void

  constructor() {
    //get cookie with name playerToken
    const playerToken = this.getCookie('playerToken')
    if (playerToken) {
      this.gameStore.playerToken = playerToken
    } else {
      window.location.href = import.meta.env.VITE_API_URL + 'become.php'
    }

    // Define a debounced wrapper for `postRequest` here
    this.debouncedPostRequest = debounce((path: string, data: any) => {
      this.postRequest(path, data)
        .then((response) => {
          logger.debug(response)
        })
        .catch((error) => {
          if (process.env.NODE_ENV === 'production') {
            Sentry.captureException(error)
          }
          logger.error(error)
        })
    }, 100) // Millisecond
  }

  /**
   * Check if the token is valid and get the user id
   */
  public async auth(): Promise<void> {
    this.gameStore.loadingText = 'Authenticating...'
    return this.getRequest('auth/api')
      .then((response) => {
        if (response) {
          this.gameStore.authPlayerId = response.data.player_id
          logger.info('Player ID: ' + this.gameStore.authPlayerId)
          this.gameStore.loadingText = 'Authenticated'
        }
      })
      .catch(() => {
        // Redirect to login
        window.location.href = import.meta.env.VITE_API_URL + 'become.php'
      })
  }

  public async postFireBaseToken(token: string): Promise<void> {
    const data = {
      token: token,
      name: navigator.userAgent
    }
    return this.postRequest('/news/notifications/add-device', data).then((response) => {
      return response
    })
  }

  public async deleteFireBaseToken(id: number): Promise<void> {
    const data = {
      id: id
    }
    return this.postRequest('/news/notifications/remove-device', data).then((response) => {
      return response
    })
  }

  public async toggleNotificationNewsType(newsType: string): Promise<void> {
    const data = {
      news_type: newsType
    }
    return this.postRequest('news/notifications/toggle-type', data).then((response) => {
      return response
    })
  }

  /**
   * Request init client data
   */
  public async syncGameState(): Promise<void> {
    return this.getRequest('data/sync-game-state')
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Purchase a ministry candidate
   *
   * @param ministryCode
   * @param candidateCode
   */
  public async purchaseMinistryCandidate(
    ministryCode: string,
    candidateCode: string
  ): Promise<void> {
    const data = {
      ministryCode: ministryCode,
      candidateCode: candidateCode
    }
    return this.postRequest('/government/cabinet/purchase', data).then((response) => {
      return response
    })
  }

  /**
   * Fire Candidate
   */
  public async fireMinister(ministerCode: string): Promise<void> {
    const data = {
      ministerCode: ministerCode
    }
    return this.postRequest('/government/cabinet/fire', data).then((response) => {
      return response
    })
  }

  /**
   * Get a battle report
   *
   * @param battleReportId
   */
  public async getBattleReport(battleReportId: number): Promise<void> {
    return this.getRequest('battle-report/' + battleReportId)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Set Spy Mission
   */
  public async setSpyMission(
    spyId: number,
    targetPlayerId: number,
    targetPlanetId: number,
    mission: string
  ): Promise<void> {
    const data = {
      spy_id: spyId,
      target_player_id: targetPlayerId,
      target_planet_id: targetPlanetId,
      mission: mission
    }
    return this.postRequest('spy/mission', data).then((response) => {
      return response
    })
  }

  /**
   * Get ally data
   */
  public async getAllyData(): Promise<any> {
    return this.getRequest('/player/relation/ally-data')
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  public async getLatestNews(fromId: number): Promise<void> {
    let path = 'news/last/30'
    if (fromId > 0) {
      path = `news/last/30/${fromId}`
    }

    // Add minShips parameter if filtering is enabled
    if (this.newsSettingsStore.hideSmallBattles) {
      const separator = path.includes('?') ? '&' : '?'
      path += `${separator}minShips=${this.newsSettingsStore.minimumShipsPerSide}`
    }

    try {
      const response = await this.getRequest(path)
      this.newsStore.firstLoad = true

      // If empty response, we have no more news
      if (response.data.length === 0) {
        this.newsStore.loadMoreDone = false
        return
      }

      response.data.forEach((newsItem: NewsItem) => {
        this.newsStore.add(newsItem)
      })
    } catch (error) {
      logger.error(error)
    }
  }

  public async setNewsRead(): Promise<void> {
    const data = {}
    return this.postRequest('news/read', data).then((response) => {
      return response
    })
  }

  /**
   * Load game assets
   */
  public async loadAssets(): Promise<void> {
    return this.getRequest('data/assets')
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Update planet population settings
   *
   * @param planetIds
   * @param populationFood
   * @param populationScience
   * @param populationProduction
   */
  public async updatePlanetPopulation(
    planetIds: Array<number>,
    populationFood: number,
    populationScience: number,
    populationProduction: number
  ): Promise<void> {
    const data = {
      planet_ids: planetIds,
      population_working_on_food_percentage: populationFood,
      population_working_on_science_percentage: populationScience,
      population_working_on_production_percentage: populationProduction
    }

    this.debouncedPostRequest('planet/population-config', data)
  }

  /**
   * Verify ship design
   *
   * @param components
   */
  public async verifyShipDesign(components: any): Promise<void> {
    const data = {
      components: components
    }
    return this.postRequest('ship-design/verify', data).then((response) => {
      return response
    })
  }

  /**
   * Create ship design
   *
   * @param shipDesignName
   * @param components
   */
  public async createShipDesign(shipDesignName: string, components: any): Promise<void> {
    const data = {
      ship_design_name: shipDesignName,
      components: components
    }
    return this.postRequest('ship-design/create', data).then((response) => {
      return response
    })
  }

  /**
   * Delete ship request
   *
   * @param shipGroupId
   */
  public async deleteShipGroup(shipGroupId: number): Promise<void> {
    return this.deleteRequest('ship-group/' + shipGroupId).then((response) => {
      return response
    })
  }

  /**
   * Delete planet group
   *
   * @param planetGroupId
   */
  public async deletePlanetGroup(planetGroupId: number): Promise<void> {
    return this.deleteRequest('planet-group/' + planetGroupId).then((response) => {
      return response
    })
  }

  /**
   * Create ship group
   *
   * @param shipGroupName
   */
  public async createShipGroup(shipGroupName: string): Promise<void> {
    const data = {
      ship_group_name: shipGroupName
    }
    return this.postRequest('ship-group', data).then((response) => {
      return response
    })
  }

  /**
   * Delete planet group
   *
   * @param planetGroupName
   */
  public async createPlanetGroup(planetGroupName: string): Promise<void> {
    const data = {
      planet_group_name: planetGroupName
    }
    return this.postRequest('planet-group', data).then((response) => {
      return response
    })
  }

  /**
   * Delete ship design
   *
   * @param shipDesignId
   */
  public async deleteShipDesign(shipDesignId: number): Promise<void> {
    return this.deleteRequest('ship-design/' + shipDesignId + '/delete').then((response) => {
      return response
    })
  }

  public async updateShipDesignName(shipDesignId: number, name: string): Promise<void> {
    const data = {
      ship_design_name: name
    }
    return this.postRequest('ship-design/' + shipDesignId + '/name', data).then((response) => {
      return response
    })
  }

  /**
   * Update planet tax configuration
   *
   * @param planetIds
   * @param taxPercentage
   */
  public async updatePlanetTax(planetIds: Array<number>, taxPercentage: number): Promise<void> {
    const data = {
      planet_ids: planetIds,
      tax_percentage: taxPercentage
    }
    this.debouncedPostRequest('planet/tax-config', data)
  }

  /**
   * Update alliance science allocation percentage
   *
   * @param percentage
   */
  public async updateAllianceSciencePercentage(percentage: number): Promise<void> {
    const data = {
      percentage: percentage
    }
    this.debouncedPostRequest('/alliance/set-science-allocation', data)
  }

  /**
   * Define what the planet is building, or add to queue
   *
   * @param planetIds
   * @param productionObjectId
   * @param queuePosition - 'after_current' | 'end_of_queue' | 'replace_clear' | 'replace_keep'
   */
  public async setPlanetsProductionObject(
    planetIds: Array<number>,
    productionObjectId: number,
    queuePosition: string = 'end_of_queue'
  ): Promise<void> {
    const data = {
      planet_ids: planetIds,
      production_object_id: productionObjectId,
      queue_position: queuePosition
    }
    return this.postRequest('planet/set-production-object', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Create Planet Queue Automation
   *
   * @param name
   */
  public async createPQA(name: string): Promise<void> {
    const data = {
      name: name
    }
    return this.postRequest('planet/pqa', data, true)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Set PQA for a planet
   *
   * @param planetIds
   * @param pqaId
   */
  public async planetSetPQA(planetIds: Array<number>, pqaId: number): Promise<void> {
    const data = {
      planetIds: planetIds,
      pqaId: pqaId
    }
    return this.postRequest('/planet/queue-automation', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Set default PQA when settling a planet
   */
  public async setPlayerSettlePQA(pqaId: number): Promise<void> {
    const data = {
      pqaId: pqaId
    }
    return this.postRequest('/government/cabinet/settlePQA', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Set default PQA when conquering a planet
   */
  public async setPlayerConquerPQA(pqaId: number): Promise<void> {
    const data = {
      pqaId: pqaId
    }
    return this.postRequest('/government/cabinet/conquerPQA', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Set player flag
   *
   * @param key The flag key
   * @param value The flag value
   */
  public async setPlayerFlag(key: string, value: string): Promise<void> {
    const data = {
      flag_key: key,
      flag_value: value
    }
    return this.postRequest('/player/set-flag', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
        throw error
      })
  }

  /**
   * Set building active limit
   *
   * @param buildingCode The building code
   * @param activeLimit The active limit (0-999)
   */
  public async setBuildingActiveLimit(buildingCode: string, activeLimit: number): Promise<void> {
    const data = {
      building_code: buildingCode,
      active_limit: activeLimit
    }
    return this.postRequest('/player/set-building-active-limit', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
        throw error
      })
  }

  /**
   * Clear production from planet
   *
   * @param planetIds
   */
  public async clearProductionFromPlanet(planetIds: Array<number>): Promise<void> {
    const data = {
      planetIds: planetIds
    }
    return this.postRequest('planet/clear-production', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Set Ore Trading
   *
   * @param fromOreId
   * @param toOreId
   * @param quantity
   */
  public async setOreTrade(fromOreId: number, toOreId: number, quantity: number): Promise<void> {
    const data = {
      fromOreId: fromOreId,
      toOreId: toOreId,
      quantity: quantity
    }
    return this.postRequest('ore/trade', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Update Planet Queue Automation
   *
   * @param id
   * @param queue
   */
  public async updatePQA(id: number, queue: number[]): Promise<void> {
    const data = {
      id: id,
      queue: queue
    }
    this.debouncedPostRequest('planet/pqa/update', data)
  }

  /**
   * Delete Planet Queue Automation
   *
   * @param id
   */
  public async deletePQA(id: number): Promise<void> {
    this.deleteRequest('planet/pqa/' + id).then((response) => {
      return response
    })
  }

  public async setTechTarget(targetTechId: number): Promise<void> {
    const data = {
      targetTechId: targetTechId
    }
    return this.postRequest('tech', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Queue a technology for research after the current technology
   *
   * @param targetTechId
   */
  public async queueTechTarget(targetTechId: number): Promise<void> {
    const data = {
      targetTechId: targetTechId,
      queue: true
    }
    return this.postRequest('tech/queue', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Update the player's tech research queue
   *
   * @param queue Array of technology IDs in the desired order
   */
  public async updateTechQueue(queue: number[]): Promise<void> {
    const data = {
      queue: queue
    }
    return this.postRequest('tech-queue', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Define what ship the planet is building, or add to queue
   *
   * @param planetIds
   * @param shipDesignId
   * @param count
   * @param fleetId
   * @param queuePosition - 'after_current' | 'end_of_queue' | 'replace_clear' | 'replace_keep'
   */
  public async setPlanetProductionObjectShip(
    planetIds: number,
    shipDesignId: number,
    count: number,
    fleetId: number,
    queuePosition: string = 'end_of_queue'
  ): Promise<void> {
    const data = {
      planet_ids: planetIds,
      ship_design_id: shipDesignId,
      count: count,
      fleet_id: fleetId,
      queue_position: queuePosition
    }
    return this.postRequest('planet/set-production-object-ship', data, true)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Cancel production object
   *
   * @param planetId
   */
  public async cancelPlanetProductionObject(planetId: number): Promise<void> {
    const data = {
      planet_id: planetId
    }
    return this.postRequest('planet/cancel-production-object', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Reorder planet production queue
   */
  public async reorderPlanetProductionQueue(
    planetId: number,
    queue: {
      id: number
      production_object_id: number
      production_ship_design_id: number
      production_ship_fleet_id: number
    }[]
  ): Promise<void> {
    const data = {
      planet_id: planetId,
      queue: queue
    }
    return this.postRequest('planet/reorder-production-queue', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * remove object from build queue
   */
  public async removeProductionObjectFromQueue(planetId: number, queueId: number): Promise<void> {
    const data = {
      planet_id: planetId,
      queue_id: queueId
    }
    return this.postRequest('planet/remove-production-object-from-queue', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Buy a building with ore (instant purchase)
   *
   * @param planetId The planet to build on
   * @param buildingCode The building code to purchase
   */
  public async buyBuildingWithOre(planetId: number, buildingCode: string): Promise<void> {
    const data = {
      planet_id: planetId,
      building_code: buildingCode
    }
    return this.postRequest('planet/buy-building-with-ore', data).then((response) => {
      return response
    })
  }

  /**
   * Delete building from planet
   *
   * @param planetId
   * @param building
   */
  public async deleteBuildingFromPlanet(planetId: number, building: string): Promise<void> {
    const data = {
      planet_id: planetId,
      building: building
    }
    return this.postRequest('planet/delete-building', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Update planet name
   *
   * @param planetId
   * @param planetName
   */
  public async updatePlanetName(planetId: number, planetName: string): Promise<void> {
    const data = {
      planet_id: planetId,
      planet_name: planetName
    }
    return this.postRequest('planet/name', data).then((response) => {
      return response
    })
  }

  /**
   * Update ship name
   * @param shipId
   * @param shipName
   */
  public async updateShipName(shipId: number, shipName: string): Promise<void> {
    const data = {
      ship_id: shipId,
      ship_name: shipName
    }
    return this.postRequest('ship/name', data).then((response) => {
      return response
    })
  }

  /**
   * Delete/Destroy Ships
   *
   * @param shipIds
   */
  public async destroyShips(shipIds: Array<bigint>): Promise<void> {
    const data = {
      shipIds: shipIds
    }
    return this.postRequest('ship/delete', data, true).then((response) => {
      return response
    })
  }

  /**
   * Abandon planet
   */
  public async abandonPlanet(planetId: number): Promise<void> {
    const data = {
      planet_id: planetId
    }
    return this.postRequest('planet/abandon', data).then((response) => {
      return response
    })
  }

  /**
   * Toggle ringworld conversion for planets
   *
   * @param planetIds
   */
  public async toggleRingworldConversion(planetIds: number[]): Promise<void> {
    const data = {
      planet_ids: planetIds
    }
    return this.postRequest('planet/toggle-ringworld-conversion', data).then((response) => {
      return response
    })
  }

  /**
   * Toggle terraforming for planets
   *
   * @param planetIds
   */
  public async toggleTerraforming(planetIds: number[]): Promise<void> {
    const data = {
      planet_ids: planetIds
    }
    return this.postRequest('planet/toggle-terraforming', data).then((response) => {
      return response
    })
  }

  /**
   * Load tooltip data
   *
   * @param path
   */
  public async loadToolTip(path: string): Promise<void> {
    return this.getRequest('tooltip/' + path)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Load planet stats
   *
   * @param planetId
   * @param type
   */
  public async loadPlanetStats(planetId: number, type: string): Promise<{ data: any }> {
    return this.getRequest('planet/stats/' + planetId + '/' + type)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Load map area
   *
   * @param x1
   * @param y1
   * @param x2
   * @param y2
   */
  public async loadMapArea(x1: number, y1: number, x2: number, y2: number): Promise<void> {
    return this.getRequest('map/load/' + x1 + '/' + y1 + '/' + x2 + '/' + y2)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Load map coordinate
   *
   * @param x
   * @param y
   */
  public async loadMapCoordinate(x: number, y: number): Promise<void> {
    return this.getRequest('map/coordinate/' + x + '/' + y)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Load planets in range
   */
  public async getPlanetsVisible(): Promise<void> {
    return this.getRequest('map/planets')
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Create fleet
   */
  public async createFleet(name: string): Promise<void> {
    const data = {
      name: name
    }
    return this.postRequest('fleet/create', data).then((response) => {
      return response
    })
  }

  /**
   * Set fleet rally point
   *
   * @param fleetId
   * @param xPos
   * @param yPos
   */
  public async fleetSetRallyPoint(fleetId: number, xPos: number, yPos: number): Promise<void> {
    const data = {
      fleetId: fleetId,
      xPos: xPos,
      yPos: yPos
    }
    return this.postRequest('fleet/set-core-rally-point', data, true).then((response) => {
      return response
    })
  }

  /**
   * Disband fleet
   */
  public async disbandFleet(fleetId: number): Promise<void> {
    return this.deleteRequest('fleet/' + fleetId, true).then((response) => {
      return response
    })
  }

  /**
   * Update fleet name
   *
   * @param fleetId
   * @param name
   */
  public async updateFleetName(fleetId: number, name: string): Promise<void> {
    const data = {
      fleetId: fleetId,
      name: name
    }
    return this.postRequest('fleet/update', data).then((response) => {
      return response
    })
  }

  /**
   * Save action queue for a fleet
   *
   * @param fleetId
   * @param actions
   */
  public async setFleetActions(fleetId: number, actions: any): Promise<void> {
    // Add comprehensive Sentry tracking for the API call
    Sentry.addBreadcrumb({
      category: 'api',
      message: 'Sending fleet actions to backend',
      level: 'info',
      data: {
        fleetId: fleetId,
        actionsCount: actions.length,
        actions: actions.map((action: any, index: number) => ({
          index: index,
          action: action.action,
          hasTargetX: 'targetX' in action,
          hasTargetY: 'targetY' in action,
          targetX: action.targetX,
          targetY: action.targetY,
          targetXType: typeof action.targetX,
          targetYType: typeof action.targetY,
          targetXIsUndefined: action.targetX === undefined,
          targetYIsUndefined: action.targetY === undefined,
          targetPlayer: action.targetPlayer,
          targetShip: action.targetShip,
          duration: action.duration,
          serialized: JSON.stringify(action)
        }))
      }
    })

    const data = {
      fleetId: fleetId,
      actions: actions
    }

    // Log the actual payload being sent
    Sentry.addBreadcrumb({
      category: 'api',
      message: 'Fleet actions payload',
      level: 'info',
      data: {
        payload: JSON.stringify(data),
        payloadSize: JSON.stringify(data).length
      }
    })

    return this.postRequest('fleet/set-actions', data, true).then((response) => {
      Sentry.addBreadcrumb({
        category: 'api',
        message: 'Fleet actions API success',
        level: 'info'
      })
      return response
    }).catch((error) => {
      // Capture the error with all context
      Sentry.captureException(error, {
        tags: {
          api_endpoint: 'fleet/set-actions',
          fleet_id: fleetId.toString()
        },
        extra: {
          actions: actions,
          payload: data
        }
      })
      throw error
    })
  }

  /**
   * Assign fleet to ships
   */
  public async setFleetForShips(fleetId: number, shipIds: any): Promise<void> {
    const data = {
      fleetId: fleetId,
      shipIds: shipIds
    }

    return this.postRequest('fleet/attach-ships', data, true).then((response) => {
      return response
    })
  }

  /**
   * Remove ships from fleet
   * @param shipIds
   */
  public async detachShipsFromFleet(shipIds: any): Promise<void> {
    const data = {
      shipIds: shipIds
    }

    return this.postRequest('fleet/detach-ships', data, true).then((response) => {
      return response
    })
  }

  /**
   * Assign group to ships
   */
  public async attachShipsToGroup(groupId: number, shipIds: any): Promise<void> {
    const data = {
      groupId: groupId,
      shipIds: shipIds
    }

    return this.postRequest('ship-group/attach-ships', data, true).then((response) => {
      return response
    })
  }

  /**
   * Assign group to planets
   */
  public async attachPlanetToGroup(groupId: number, planetIds: any): Promise<void> {
    const data = {
      groupId: groupId,
      planetIds: planetIds
    }

    return this.postRequest('planet-group/attach-planets', data, true).then((response) => {
      return response
    })
  }

  /**
   * Remove planets from group
   */
  public async detachPlanetsFromGroup(planetIds: any): Promise<void> {
    const data = {
      planetIds: planetIds
    }

    return this.postRequest('planet-group/detach-planets', data, true).then((response) => {
      return response
    })
  }

  /**
   * Remove ships from group
   */
  public async detachShipsFromGroup(shipIds: any): Promise<void> {
    const data = {
      shipIds: shipIds
    }

    return this.postRequest('ship-group/detach-ships', data, true).then((response) => {
      return response
    })
  }

  /**
   * Save action queue for a ship
   *
   * @param ships
   * @param actions
   */
  public async setShipActions(ships: any, actions: any): Promise<void> {
    const data = {
      ships: ships,
      actions: actions
    }

    return this.postRequest('ship/set-actions', data, true).then((response) => {
      return response
    })
  }

  /**
   * Clear action queue for a ship
   *
   * @param ships
   */
  public async clearShipActions(ships: any): Promise<void> {
    const data = {
      ships: ships
    }

    return this.postRequest('ship/clear-actions', data, true).then((response) => {
      return response
    })
  }

  /**
   * Get queued actions for a ship
   *
   * @param shipId
   */
  public async getShipQueuedActions(shipId: number): Promise<void> {
    return this.getRequest('ship/queued-actions/' + shipId)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  public async getStatsDataForKey(key: string): Promise<void> {
    return this.getRequest('stats/' + key)
      .then((response) => {
        return response.data
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  public async getFinancialIncomeChartData(): Promise<void> {
    return this.getRequest('chart/financial-income')
      .then((response) => {
        return response.data
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Declare war
   *
   * @param playerId
   */
  public async relationDeclareWar(playerId: number): Promise<void> {
    const data = {
      playerId: playerId
    }

    return this.postRequest('player/relation/declare-war', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * request/accept friendship
   *
   * @param playerId
   * @param type
   */
  public async relationRequest(playerId: number, type: string): Promise<void> {
    const data = {
      playerId: playerId,
      type: type
    }

    return this.postRequest('player/relation/request', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * reject friendship request
   */
  public async relationReject(playerId: number, type: string): Promise<void> {
    const data = {
      playerId: playerId,
      type: type
    }

    return this.postRequest('player/relation/reject-request', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Create a new alliance with the given name and description
   *
   * @param allianceName The name for the new alliance
   * @param allianceDescription Optional description for the alliance
   */
  public async createAlliance(allianceName: string, allianceDescription: string): Promise<void> {
    const data = {
      name: allianceName,
      description: allianceDescription
    }
    return this.postRequest('alliance/create', data).then((response) => {
      return response
    })
  }

  /**
   * Leave an alliance (as a member)
   * Or disband an alliance (as the leader)
   */
  public async leaveAlliance(): Promise<void> {
    return this.postRequest('alliance/leave', {}).then((response) => {
      return response
    })
  }

  /**
   * Update alliance description
   *
   * @param description New description for the alliance
   */
  public async updateAllianceDescription(description: string): Promise<void> {
    const data = {
      description: description
    }
    return this.postRequest('alliance/update/description', data).then((response) => {
      return response
    })
  }

  /**
   * Request to join an alliance
   *
   * @param allianceId The ID of the alliance to request to join
   */
  public async requestJoinAlliance(allianceId: number): Promise<void> {
    const data = {
      alliance_id: allianceId
    }
    return this.postRequest('alliance/join-request', data).then((response) => {
      return response
    })
  }

  /**
   * Accept a player's request to join the alliance
   *
   * @param playerId The ID of the player to accept
   */
  public async acceptAllianceJoinRequest(playerId: number): Promise<void> {
    const data = {
      player_id: playerId
    }
    return this.postRequest('alliance/accept', data).then((response) => {
      return response
    })
  }

  /**
   * Decline a player's request to join the alliance
   *
   * @param playerId The ID of the player to decline
   */
  public async declineAllianceJoinRequest(playerId: number): Promise<void> {
    const data = {
      player_id: playerId
    }
    return this.postRequest('alliance/decline', data).then((response) => {
      return response
    })
  }

  /**
   * Revoke a pending request to join an alliance
   *
   * @param allianceId The ID of the alliance to revoke the join request from
   */
  public async revokeJoinAlliance(allianceId: number): Promise<void> {
    const data = {
      alliance_id: allianceId
    }
    return this.postRequest('alliance/revoke-join-request', data).then((response) => {
      return response
    })
  }

  /**
   * Kick a member from the alliance (leader only)
   *
   * @param playerId The ID of the player to kick from the alliance
   */
  public async kickAllianceMember(playerId: number): Promise<void> {
    const data = {
      player_id: playerId
    }
    return this.postRequest('alliance/kick', data).then((response) => {
      return response
    })
  }

  /**
   * Promote a member to alliance leader
   *
   * @param playerId The ID of the player to promote to leader
   */
  public async promoteAllianceMember(playerId: number): Promise<void> {
    const data = {
      player_id: playerId
    }
    return this.postRequest('alliance/promote', data).then((response) => {
      return response
    })
  }

  /**
   * Set a prototype to be worked on by the alliance (alliance leader only)
   *
   * @param prototypeId The ID of the prototype/tech to set as alliance research target
   */
  public async setAlliancePrototype(prototypeId: number): Promise<void> {
    const data = {
      prototype_id: prototypeId
    }
    return this.postRequest('alliance/set-prototype', data).then((response) => {
      return response
    })
  }

  /**
   * Update alliance image
   *
   * @param image The image to update the alliance with
   */
  public async updateAllianceImage(image: FormData): Promise<void> {
    return this.postRequest('alliance/update/image', image, true, true).then((response) => {
      return response
    })
  }

  /**
   * Stop the NAP auto-renewal with another player
   *
   * @param playerId The player ID to stop the NAP auto-renewal with
   */
  public async stopNAPRenewal(playerId: number): Promise<void> {
    const data = {
      playerId: playerId
    }

    return this.postRequest('player/relation/stop-nap-renewal', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Create private chat room
   * /chat/room/private/create
   */
  public async createPrivateChatRoom(
    otherPlayerId: number
  ): Promise<{ data: { chatRoom: ChatRoom } }> {
    const data = {
      otherPlayerId: otherPlayerId
    }

    return this.postRequest('chat/room/private/create', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
        throw error
      })
  }

  /**
   * Post message in chat room (with optional image)
   */
  public async postMessageInRoom(roomId: number, message: string, imageFile?: File): Promise<void> {
    const formData = new FormData()
    formData.append('roomId', roomId.toString())
    formData.append('message', message)
    if (imageFile) {
      formData.append('image', imageFile)
    }

    return this.postRequest('chat/room/message/post', formData, false, true)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
        throw error
      })
  }

  /**
   * Set chat room as read
   */
  public async setChatRoomAsRead(roomId: number, messageId: number): Promise<void> {
    const data = {
      roomId: roomId,
      messageId: messageId
    }

    return this.postRequest('chat/room/read', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Get chat room messages
   * @param roomId
   * @param fromMessageId
   * @param limit
   */
  public async postRoomMessages(
    roomId: number,
    fromMessageId: number,
    limit: number
  ): Promise<void> {
    const data = {
      roomId: roomId,
      fromMessageId: fromMessageId,
      limit: limit
    }

    return this.postRequest('chat/room/messages', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Get chat room participants
   */
  public async postRoomParticipants(roomId: number): Promise<void> {
    const data = {
      roomId: roomId
    }

    return this.postRequest('chat/room/participants', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Mute a player
   * /chat/player/mute
   */
  public async mutePlayer(playerId: number): Promise<{ data: { success: boolean; mutedPlayerId: number } }> {
    const data = {
      playerId: playerId
    }

    return this.postRequest('chat/player/mute', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
        throw error
      })
  }

  /**
   * Unmute a player
   * /chat/player/unmute
   */
  public async unmutePlayer(playerId: number): Promise<{ data: { success: boolean; unmutedPlayerId: number } }> {
    const data = {
      playerId: playerId
    }

    return this.postRequest('chat/player/unmute', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
        throw error
      })
  }

  /**
   * Set player color label for map territory
   * /player/set-color-label
   */
  public async setPlayerColorLabel(labeledPlayerId: number, color: string): Promise<void> {
    const data = {
      labeled_player_id: labeledPlayerId,
      color: color
    }

    return this.postRequest('player/set-color-label', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
        throw error
      })
  }

  /**
   * Delete player color label for map territory
   * /player/delete-color-label
   */
  public async deletePlayerColorLabel(labeledPlayerId: number): Promise<void> {
    const data = {
      labeled_player_id: labeledPlayerId
    }

    return this.postRequest('player/delete-color-label', data)
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
        throw error
      })
  }

  /**
   * Get alliance statistics
   */
  public async getAllianceStats(): Promise<any> {
    return this.getRequest('alliance/stats')
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
        throw error
      })
  }

  /**
   * Ping endpoint to keep session alive
   */
  public async ping(): Promise<void> {
    return this.getRequest('ping')
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
      })
  }

  /**
   * Sanitize error for display - removes sensitive information
   */
  private sanitizeError(error: any): string {
    try {
      if (typeof error === 'string') {
        return error.replace(/x-auth-token[:\s]+[^\s,}]+/gi, 'x-auth-token: [REDACTED]')
      }
      
      if (error?.response?.data?.message) {
        return error.response.data.message
      }
      
      if (error?.message) {
        return error.message.replace(/x-auth-token[:\s]+[^\s,}]+/gi, 'x-auth-token: [REDACTED]')
      }
      
      // For complex objects, convert to string and sanitize
      const errorStr = JSON.stringify(error, null, 2)
      return errorStr.replace(/("x-auth-token":\s*")[^"]+(")/gi, '$1[REDACTED]$2')
        .replace(/x-auth-token[:\s]+[^\s,}]+/gi, 'x-auth-token: [REDACTED]')
    } catch (e) {
      return 'An error occurred but could not be displayed safely'
    }
  }

  /**
   * Post Request
   *
   * @param path
   * @param data
   * @param longRequest
   * @param isFormData
   * @protected
   */
  protected postRequest(
    path: string,
    data: any,
    longRequest: boolean = false,
    isFormData: boolean = false
  ): Promise<any> {
    const headers: Record<string, string> = {
      'X-Auth-Token': this.gameStore.playerToken
    }

    let requestId = ''
    if (longRequest) {
      requestId = this.gameStore.setPendingRequest()
      headers['Request-Id'] = requestId
    }

    // Don't set Content-Type for FormData, let the browser set it with the boundary
    if (!isFormData) {
      headers['Content-Type'] = 'application/json'
    }

    return axios
      .post(this.baseUrl + path, data, {
        headers: headers
      })
      .then((response) => {
        return response
      })
      .catch((error) => {
        // Clear pending request on error
        if (longRequest && requestId) {
          this.gameStore.clearPendingRequest(requestId)
        }
        
        if (error.response?.status && error.response.status === 423) {
          this.gameStore.modalType = 'hoursOnline'
          this.gameStore.modalData = {}
        } else if (error.response?.status && error.response.status === 500) {
          if (process.env.NODE_ENV === 'production') {
            Sentry.captureException(error)
          }
          logger.error(error)
          this.gameStore.gameOutOfSync = true
          this.gameStore.backendError = this.sanitizeError(error)
          // Hide loading popup when error occurs
          this.gameStore.pendingRequestPopupVisible = false
        } else if (!error.response?.status) {
          // Network errors are client-side connectivity issues, not application bugs
          // Just show user feedback, don't log to Sentry
          this.gameStore.setTinyErrorToastMessage('Network error, please try again')
        }
        throw error
      })
  }

  /**
   * Get Request
   *
   * @param path
   * @protected
   */
  protected getRequest(path: string): Promise<any> {
    logger.debug('getRequest to ' + path)
    return axios
      .get(this.baseUrl + path, {
        headers: {
          'X-Auth-Token': this.gameStore.playerToken
        }
      })
      .then((response) => {
        return response
      })
      .catch((error) => {
        logger.error(error)
        if (error.response?.status && error.response.status === 423) {
          this.gameStore.modalType = 'hoursOnline'
          this.gameStore.modalData = {}
        } else if (error.response.status && error.response.status === 500) {
          if (process.env.NODE_ENV === 'production') {
            Sentry.captureException(error)
          }
          this.gameStore.gameOutOfSync = true
          this.gameStore.backendError = this.sanitizeError(error)
          // Hide loading popup when error occurs
          this.gameStore.pendingRequestPopupVisible = false
          throw error
        } else if (!error.response.status) {
          // Network errors are client-side connectivity issues, not application bugs
          // Just show user feedback, don't log to Sentry
          this.gameStore.setTinyErrorToastMessage('Network error, please try again')
        }
        throw error
      })
  }

  /**
   * Delete Request
   *
   * @param path
   * @param longRequest
   * @protected
   */
  protected deleteRequest(path: string, longRequest: boolean = false): Promise<any> {
    /*console.log('deleteRequest to ' + path)*/
    
    let requestId = ''
    if (longRequest) {
      requestId = this.gameStore.setPendingRequest()
    }
    
    return axios
      .delete(this.baseUrl + path, {
        headers: {
          'X-Auth-Token': this.gameStore.playerToken,
          'Request-Id': longRequest ? requestId : ''
        }
      })
      .then((response) => {
        return response
      })
      .catch((error) => {
        // Clear pending request on error
        if (longRequest && requestId) {
          this.gameStore.clearPendingRequest(requestId)
        }
        
        if (error.response?.status && error.response.status === 423) {
          this.gameStore.modalType = 'hoursOnline'
          this.gameStore.modalData = {}
        } else if (error.response.status && error.response.status === 500) {
          if (process.env.NODE_ENV === 'production') {
            Sentry.captureException(error)
          }
          this.gameStore.gameOutOfSync = true
          this.gameStore.backendError = this.sanitizeError(error)
          // Hide loading popup when error occurs
          this.gameStore.pendingRequestPopupVisible = false
          throw error
        } else if (!error.response.status) {
          // Network errors are client-side connectivity issues, not application bugs
          // Just show user feedback, don't log to Sentry
          this.gameStore.setTinyErrorToastMessage('Network error, please try again')
        }
        throw error
      })
  }

  /**
   * Handle Error
   */
  protected handleError(error: AxiosResponse): void {
    alert(error)
  }

  protected getCookie(name: string): string | null {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null
    return null
  }
}
